import { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import { useAuth } from '@/contexts/AuthContext';
import PlusUpsellModal from '@/components/PlusUpsellModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 104 (Jun 2026) — ETF Top Holdings from SEC EDGAR Form N-PORT.
// Renders inside the Stock Metrics tab fund section, matching the
// existing "floating card" style (rounded-lg border bg-card).
const fmtVal = (v) => {
  if (v == null) return '--';
  const abs = Math.abs(v);
  if (abs >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  return `$${Math.round(v).toLocaleString()}`;
};

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
};

// Round 104e — shared bar-list card used by the Asset Allocation and
// Sector Breakdown fallbacks. Matches the legacy StockMetricsTab card
// styling (primary bars for allocation, blue for sectors).
const BreakdownCard = ({ title, entries, barClass, caption, testId, useSectorTip }) => {
  const items = Object.entries(entries || {});
  if (!items.length) return null;
  const maxVal = Math.max(...items.map(([, v]) => v)) || 1;
  return (
    <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid" data-testid={testId}>
      <h4 className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400 mt-4 mb-1.5">{title}</h4>
      {items.map(([label, pct]) => (
        <div key={label} className="flex items-center justify-between py-1 border-b border-border/20 last:border-0">
          <span className="text-xs font-bold text-muted-foreground">
            {useSectorTip ? <AbbrTip term="SECTOR">{label}</AbbrTip> : label}
          </span>
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full ${barClass}`} style={{ width: `${Math.min((pct / maxVal) * 100, 100)}%` }} />
            </div>
            <span className="text-xs font-mono font-semibold w-12 text-right">{pct}%</span>
          </div>
        </div>
      ))}
      {caption && <div className="pt-1 pb-0.5 text-[9px] text-muted-foreground">{caption}</div>}
    </div>
  );
};

export const EtfTopHoldingsCard = ({ ticker, onSelectTicker, warmAlloc, warmSectors }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // PAYWALL FLIP: admin || plus
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setExpanded(false);
    axios.get(`${API}/stocks/${ticker}/etf-holdings`, { withCredentials: true })
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ available: false }); });
    return () => { alive = false; };
  }, [ticker]);

  const nport = data && data.available ? data : null;
  const topHoldings = nport?.top_holdings || [];

  // Warmer `holdings_data` preferred (full-fund figures straight from
  // the fund provider); N-PORT-derived breakdowns fill every gap so
  // ETFs the warmer never touched (e.g. GSEW) still get all 3 cards.
  const hasWarmAlloc = warmAlloc && Object.keys(warmAlloc).length > 0;
  const allocEntries = hasWarmAlloc
    ? warmAlloc
    : Object.fromEntries(Object.entries(nport?.asset_breakdown || {}).filter(([, v]) => v >= 0.1));
  // Degenerate warmer sector maps (a single sector at ~100%, e.g.
  // bond funds mis-tagged "Financial Services 100%") are junk —
  // prefer the N-PORT estimate (or nothing) over them.
  const warmSectorItems = Object.entries(warmSectors || {});
  const warmSectorsReliable =
    warmSectorItems.length > 1 ||
    (warmSectorItems.length === 1 && warmSectorItems[0][1] < 99.5);
  const hasWarmSectors = warmSectorItems.length > 0 && warmSectorsReliable;
  const sectorEntries = hasWarmSectors ? warmSectors : (nport?.sector_breakdown_computed?.sectors || {});
  const sectorCaption = hasWarmSectors ? null : 'Estimated from the fund\u2019s resolved top-50 holdings (SEC N-PORT)';

  if (!Object.keys(allocEntries).length && !Object.keys(sectorEntries).length && !topHoldings.length) return null;

  const rows = expanded ? topHoldings.slice(0, 25) : topHoldings.slice(0, 10);
  const maxPct = topHoldings[0]?.pct_val || 1;

  return (
    <>
      {Object.keys(allocEntries).length > 0 && (
        <BreakdownCard title="Asset Allocation" entries={allocEntries} barClass="bg-primary/70" testId="etf-asset-allocation-card" />
      )}
      {Object.keys(sectorEntries).length > 0 && (
        <BreakdownCard title="Sector Breakdown" entries={sectorEntries} barClass="bg-blue-500/70" caption={sectorCaption} useSectorTip testId="etf-sector-breakdown-card" />
      )}
      {topHoldings.length > 0 && (
      <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid" data-testid="etf-top-holdings-card">
      <div className="flex items-baseline justify-between mt-4 mb-1.5">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">Top Holdings</h4>
        {data.report_date && (
          <span className="text-[10px] text-muted-foreground" data-testid="etf-holdings-asof">as of {fmtDate(data.report_date)}</span>
        )}
      </div>

      {/* Fund-level stats strip */}
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 pb-1.5 border-b border-border/30">
        {data.net_assets != null && (
          <span className="text-[11px] text-muted-foreground">Net Assets <span className="font-mono font-semibold text-foreground" data-testid="etf-holdings-net-assets">{fmtVal(data.net_assets)}</span></span>
        )}
        {data.holdings_count != null && (
          <span className="text-[11px] text-muted-foreground">Positions <span className="font-mono font-semibold text-foreground" data-testid="etf-holdings-count">{data.holdings_count.toLocaleString()}</span></span>
        )}
        {data.top10_weight != null && (
          <span className="text-[11px] text-muted-foreground">Top 10 <span className="font-mono font-semibold text-foreground" data-testid="etf-holdings-top10-weight">{data.top10_weight.toFixed(1)}%</span></span>
        )}
      </div>

      {rows.map((h, i) => {
        // Jul 2026 (Plus paywall) — non-dividend holdings (or holdings
        // outside the dividend DB) are Plus-gated: the rank number is
        // replaced by a gold lock and the row opens the upsell modal.
        const locked = !!h.holding_ticker && h.pays_dividend !== true && !isAdmin;
        const clickable = !!h.holding_ticker && !!onSelectTicker;
        return (
          <div
            key={`${h.cusip || h.name}-${i}`}
            className={`flex items-center gap-2 py-1 border-b border-border/20 last:border-0 ${clickable ? 'cursor-pointer hover:bg-muted/40 -mx-1 px-1 rounded transition-colors' : ''}`}
            onClick={clickable ? () => (locked ? setUpsellOpen(true) : onSelectTicker(h.holding_ticker)) : undefined}
            title={locked ? `${h.holding_ticker} is a non-dividend stock — unlock the full market with DiviDrip Plus` : undefined}
            data-testid={locked ? `etf-holding-row-locked-${i}` : `etf-holding-row-${i}`}
          >
            {locked ? (
              <span className="w-4 flex justify-end shrink-0" data-testid={`etf-holding-lock-${i}`}>
                <Lock className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
              </span>
            ) : (
              <span className="text-[10px] font-mono text-muted-foreground w-4 text-right shrink-0">{i + 1}</span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5">
                {h.holding_ticker && (
                  <span className="text-xs font-mono font-bold text-primary shrink-0">{h.holding_ticker}</span>
                )}
                <span className="text-xs font-medium truncate" title={h.name}>{h.name}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] font-mono text-muted-foreground hidden sm:inline w-14 text-right">{fmtVal(h.value_usd)}</span>
              <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-fuchsia-500/70" style={{ width: `${Math.min(((h.pct_val || 0) / maxPct) * 100, 100)}%` }} />
              </div>
              <span className="text-xs font-mono font-semibold w-12 text-right">{h.pct_val != null ? `${h.pct_val.toFixed(2)}%` : '--'}</span>
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between pt-1.5 pb-1">
        {data.top_holdings.length > 10 ? (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] font-semibold text-primary hover:underline"
            data-testid="etf-holdings-expand-btn"
          >
            {expanded ? 'Show top 10' : `Show top ${Math.min(25, data.top_holdings.length)}`}
          </button>
        ) : <span />}
        <span className="text-[9px] text-muted-foreground">Source: SEC EDGAR Form N-PORT</span>
      </div>
      <PlusUpsellModal open={upsellOpen} onClose={() => setUpsellOpen(false)} />
    </div>
      )}
    </>
  );
};
